import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentService } from '../../services/paymentService';
import bankAccountService from '../../services/bankAccountService';
import { Invoice } from '../../types/invoice';
import { CreatePaymentData, PAYMENT_METHODS } from '../../types/payment';
import { formatCurrency } from '../../utils/formatters';

interface PaymentFormProps {
  invoice?: Invoice;
  customerId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
  invoice,
  customerId,
  onSuccess,
  onCancel
}) => {
  const queryClient = useQueryClient();
  
  // Fetch bank accounts for selection
  const { data: bankAccountsResponse, isLoading: bankAccountsLoading } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => bankAccountService.getBankAccounts({ isActive: true })
  });
  
  const [formData, setFormData] = useState<CreatePaymentData>({
    amount: invoice?.remainingAmount || 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank_transfer',
    reference: '',
    notes: '',
    customerId: customerId || invoice?.customer?.id || '',
    invoiceId: invoice?.id || '',
    bankAccountId: ''
  });

  const [errors, setErrors] = useState<string[]>([]);

  const createPaymentMutation = useMutation({
    mutationFn: paymentService.createPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', invoice?.id] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      onSuccess();
    },
    onError: (error: any) => {
      console.error('Payment creation failed:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to create payment';
      setErrors([errorMessage]);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    const validationErrors = paymentService.validatePaymentData(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Additional validation for invoice payments
    if (invoice && formData.amount > invoice.remainingAmount) {
      setErrors(['Payment amount cannot exceed remaining balance']);
      return;
    }

    // Validate bank account selection
    if (!formData.bankAccountId) {
      setErrors(['Please select a bank account']);
      return;
    }

    setErrors([]);
    createPaymentMutation.mutate(formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px', background: '#f4efe2',
    border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
    fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
    outline: 'none', colorScheme: 'light' as any, boxSizing: 'border-box' as const,
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
    fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 6,
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px 16px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033', margin: 0 }}>
          {invoice ? `Add Payment — ${invoice.invoiceNumber}` : 'Add Payment'}
        </h3>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '24px 28px' }}>
        {/* Invoice summary */}
        {invoice && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center', background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: '12px 16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[
                ['Customer', invoice.customer?.name],
                ['Invoice Total', formatCurrency(invoice.total)],
                ['Paid Amount', formatCurrency(invoice.paidAmount)],
              ].map(([k, v]) => (
                <div key={k} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                  <span style={{ color: '#6b6a5c' }}>{k}: </span>{v}
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 4 }}>Outstanding</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 300, color: '#c95a5a' }}>
                {formatCurrency(invoice.remainingAmount)}
              </div>
            </div>
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <div style={{ background: 'rgba(201,90,90,0.08)', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 4, padding: 12 }}>
            <ul style={{ margin: 0, paddingLeft: 16, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#c95a5a' }}>
              {errors.map((error, index) => <li key={index}>{error}</li>)}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Payment Amount <span style={{ color: '#c95a5a' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', pointerEvents: 'none' }}>BD</span>
                <input
                  type="number"
                  name="amount"
                  step="0.01"
                  min="0"
                  max={invoice?.remainingAmount}
                  value={formData.amount}
                  onChange={handleInputChange}
                  style={{ ...inputStyle, paddingLeft: 30 }}
                  required
                />
              </div>
              {invoice && (
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                  Maximum: {formatCurrency(invoice.remainingAmount)}
                </p>
              )}
            </div>

            <div>
              <label style={labelStyle}>Payment Date <span style={{ color: '#c95a5a' }}>*</span></label>
              <input
                type="date"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleInputChange}
                style={inputStyle}
                required
              />
            </div>

            <div>
              <label style={labelStyle}>Payment Method <span style={{ color: '#c95a5a' }}>*</span></label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                style={{ ...inputStyle, cursor: 'pointer' }}
                required
              >
                {PAYMENT_METHODS.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Bank Account <span style={{ color: '#c95a5a' }}>*</span></label>
              <select
                name="bankAccountId"
                value={formData.bankAccountId}
                onChange={handleInputChange}
                style={{ ...inputStyle, cursor: 'pointer' }}
                required
                disabled={bankAccountsLoading}
              >
                <option value="">Select a bank account</option>
                {bankAccountsResponse?.accounts?.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.accountName} - {account.bankName} ({formatCurrency(account.balance)})
                  </option>
                ))}
              </select>
              {bankAccountsLoading && (
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>Loading bank accounts...</p>
              )}
            </div>

            <div>
              <label style={labelStyle}>Reference Number</label>
              <input
                type="text"
                name="reference"
                value={formData.reference}
                onChange={handleInputChange}
                placeholder="e.g., Check #1234, Transaction ID"
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Notes</label>
            <textarea
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Additional notes about this payment..."
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
            <button type="button" onClick={onCancel} style={{ padding: '10px 24px', background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={createPaymentMutation.isPending} style={{ padding: '10px 24px', background: createPaymentMutation.isPending ? 'rgba(92,78,51,0.25)' : 'linear-gradient(135deg, #d97706, #b45309)', border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: createPaymentMutation.isPending ? '#c9c2ab' : '#1c2033', cursor: createPaymentMutation.isPending ? 'not-allowed' : 'pointer' }}>
              {createPaymentMutation.isPending ? 'Processing...' : 'Add Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};